import { takeEvery, all, put, select } from 'redux-saga/effects';
import firestoreRedux from '@dreamworld/firestore-redux';

import * as actions from './actions.js';
import * as selectors from './selectors.js';
import * as cardSelectors from '../card/selectors.js';
// Direct path, and only the action types: `card-details/index.js` runs its module registration
// on load, and this module is imported from `store.js` before the store exists.
import * as cardDetailsActions from '../card-details/actions.js';
import { cardScopeFilter, translationId } from './utils.js';

/**
 * Reads the open card in the language the reader picked: sets the library's Translation language,
 * then starts an activation scoped to this card's own documents.
 *
 * The language is app-wide in the library, which holds because only the card on top stays
 * translated - see `stopOthersOnCardOpened`.
 *
 * Starting an activation that is already started reconciles its scope instead of failing, so
 * picking a second language re-translates the card in place rather than needing a stop first.
 */
function* translateCard(action) {
  try {
    const state = yield select();
    const tagIds = cardSelectors.tags(state, action.cardId);
    const columnId = firestoreRedux.selectors.doc(state, 'cards', action.cardId)?.columnId;
    firestoreRedux.translation.setLanguage(action.lang);
    firestoreRedux.translation.start({
      id: translationId(action.cardId),
      filterFunction: cardScopeFilter(action.cardId, { tagIds, columnId }),
    });
  } catch (error) {
    console.error('card-translate: could not translate the card', error);
  }
}

/**
 * Puts the author's words back: stops that card's activation, and every read falls back to the
 * stored document immediately - nothing has to be fetched to undo a translation.
 */
function* stopCardTranslation(action) {
  try {
    firestoreRedux.translation.stop(translationId(action.cardId));
  } catch (error) {
    console.error('card-translate: could not stop the card translation', error);
  }
}

/**
 * A translation lasts exactly as long as the reader is looking at the card: every model call it
 * costs is for a card nobody has open otherwise.
 *
 * Watches the card's own dialog actions rather than the components, because a card leaves the
 * screen through more than one route - the close icon, Escape, the browser's Back, a board the
 * reader loses access to - and they all end in `DIALOG_CLOSED`.
 */
function* stopOnCardClosed({ cardId }) {
  // Nothing to undo on a card that was never translated, and dispatching anyway would hand every
  // connected component a new state on every card close.
  const language = yield select((state) => selectors.translatedLanguage(state, cardId));
  if (language) {
    yield put(actions.showOriginal(cardId));
  }
}

/**
 * A card opened on top of a translated one (the desktop dialog stack) hides it, so that card's
 * translation stops too.
 */
function* stopOthersOnCardOpened({ cardId }) {
  const cards = yield select((state) => state['card-translate']?.cards || {});
  for (const translatedCardId of Object.keys(cards)) {
    if (translatedCardId !== cardId) {
      yield put(actions.showOriginal(translatedCardId));
    }
  }
}

/**
 * Init Saga.
 */
export function* cardTranslateSaga() {
  yield all([
    takeEvery(actions.TRANSLATE, translateCard),
    takeEvery(actions.SHOW_ORIGINAL, stopCardTranslation),
    takeEvery(cardDetailsActions.DIALOG_CLOSED, stopOnCardClosed),
    takeEvery(cardDetailsActions.DIALOG_OPENED, stopOthersOnCardOpened)
  ]);
}
